import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaFilter } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import { useGetProductListQuery } from "../../utils/api/product/product.api";
import { usePagination } from "../../utils/hooks";
import type { Product } from "../../utils/api/product/product.type";
import { QueryUrl } from "../../utils/Utils";
import { BestSellerCard } from "../HomePage/BestSellerCard";
import FrontFooter from "../shared/FrontFooter/FrontFooter";
import FrontHeader from "../shared/FrontHeader/FrontHeader";
import FrontMainNav from "../shared/FrontMainNav/FrontMainNav";
import FilterItems from "../user/ParcoursAchat/FilterItems";
import FilterMobileItem from "../user/ParcoursAchat/modal/FilterMobileItem";
import { ProductItemsSkeleton } from "../user/ParcoursAchat/ParcoursAchat";

let options = [
  { label: "Nombre d'avis", value: "avreageRate" },
  { label: "Date de création", value: "createdAt" },
  { label: "Nom", value: "name" },
  { label: "Note moyenne", value: "rateCount" },
  { label: "Prix", value: "price" },
];

const LIMIT = 9;

function OurProduct() {
  const { page, setPage } = usePagination(LIMIT);
  const [searchParams, setSearchParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleShow = () => setShow(true);
  const [query, setQuery] = React.useState({
    categorie: searchParams?.get("categorie") || "",
    sous_categorie: searchParams?.get("sous_categorie") || "",
    prix_min: searchParams?.get("prix_min") || "",
    prix_max: searchParams?.get("prix_max") || "",
    orderBy: searchParams?.get("orderBy") || "",
  });

  const queryArgs = React.useMemo(
    () => ({
      page,
      limit: LIMIT,
      prix_min: searchParams?.get("prix_min") || "",
      prix_max: searchParams?.get("prix_max") || "",
      categorie: searchParams?.get("categorie") || "",
      sous_categorie: (() => {
        const raw = searchParams?.get("sous_categorie");
        if (!raw) return "";
        try {
          return JSON.parse(raw);
        } catch {
          return "";
        }
      })(),
    }),
    [
      page,
      searchParams?.get("prix_min"),
      searchParams?.get("prix_max"),
      searchParams?.get("categorie"),
      searchParams?.get("sous_categorie"),
    ]
  );

  const { data = { results: [], count: 0 }, isFetching } =
    useGetProductListQuery(queryArgs);

  const totalCount = data?.count ?? 0;
  const hasMore =
    (data?.results?.length ?? 0) > 0 && allProducts.length < totalCount;

  const lastSyncedRef = useRef<{ page: number; count: number } | null>(null);

  useEffect(() => {
    if (!data?.results) return;
    const count = data?.count ?? 0;
    if (
      lastSyncedRef.current?.page === page &&
      lastSyncedRef.current?.count === count
    ) {
      return;
    }
    lastSyncedRef.current = { page, count };
    if (page === 1) {
      setAllProducts(data.results);
    } else {
      setAllProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p?.id));
        const newItems = data.results.filter(
          (p) => p?.id != null && !existingIds.has(p.id)
        );
        return newItems.length ? [...prev, ...newItems] : prev;
      });
    }
  }, [page, data]);

  const priceExtremes = React.useMemo(() => {
    if (!allProducts.length) return null;
    let min = Number.POSITIVE_INFINITY;
    let max = 0;
    for (const p of allProducts) {
      const raw = (p as any)?.prix ?? (p as any)?.price ?? null;
      const price = raw != null ? Number(raw) : NaN;
      if (!Number.isFinite(price)) continue;
      if (price < min) min = price;
      if (price > max) max = price;
    }
    if (!Number.isFinite(min)) return null;
    return { min, max };
  }, [allProducts]);

  const filtersKey =
    (searchParams?.get("categorie") ?? "") +
    (searchParams?.get("sous_categorie") ?? "") +
    (searchParams?.get("prix_min") ?? "") +
    (searchParams?.get("prix_max") ?? "");

  useEffect(() => {
    setPage(1);
    setAllProducts([]);
    lastSyncedRef.current = null;
  }, [filtersKey]);

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    setPage((p) => p + 1);
  }, [isFetching, hasMore, setPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px", threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <>
      <div className="landing-page-component">
        <FrontHeader />
        <div className="page-content">
          <FrontMainNav />
          <div className="container-easy-market">
            <div className="row row-achat-page mb-5">
              <FilterItems
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                query={query}
                setQuery={setQuery}
                setPage={setPage}
                minAvailablePrice={priceExtremes?.min}
                maxAvailablePrice={priceExtremes?.max}
              />
              <div className="container-filter-parcours-achat-mobile no-view-desktop">
                <button
                  className="btn btn-filter-mobile"
                  type="button"
                  onClick={() => handleShow()}
                >
                  <FaFilter />
                  <span className="ps-2">Filtrer</span>
                </button>
              </div>

              <div className="col-lg-9 col-md-7 col-right-achat-page">
                <div className="content-col-right-achat-page">
                  <div className="container-home-page mb-3 our-product-container-homepage our-product-grid-3 best-sellers-section">
                    <div className="best-sellers-section-container mt-3">

                      {/* Bouton "Tous les produits" visible quand une catégorie est sélectionnée */}
                      {searchParams.get("categorie") && (
                        <div className="d-flex justify-content-center mt-2 mb-3">
                         <Link
  to="/produits"
  className="btn btn-purple btn-sm d-flex align-items-center gap-2 px-3 py-2 mt-3"
  onClick={() => {
    setSearchParams(
      QueryUrl("", {
        ...query,
        categorie: "",
        sous_categorie: [],
      })
    );
    setPage(1);
  }}
>
  <i className="bi bi-arrow-left"></i>
  Tous les produits
</Link>
                        </div>
                      )}

                      <div className="best-sellers-section-grid our-product-grid py-4">
                        {page === 1 && isFetching && <ProductItemsSkeleton />}
                        {allProducts.length > 0 &&
                          allProducts.map((product, index) => (
                            <div
                              key={
                                product?.id != null
                                  ? `product-${product.id}-${index}`
                                  : `product-fallback-${index}`
                              }
                              className="best-sellers-section-grid-item"
                            >
                              <BestSellerCard item={product} />
                            </div>
                          ))}
                      </div>

                      {!isFetching && allProducts.length === 0 && (
                        <div className="col-sm-12 col-md-12 mb-3 m-t-36 d-flex justify-content-center">
                          <p>Aucun produit disponible</p>
                        </div>
                        
                      )}

                      <div
                        ref={sentinelRef}
                        className="infinite-scroll-sentinel"
                        aria-hidden="true"
                      />

                      {isFetching && page > 1 && hasMore && (
                        <div className="d-flex justify-content-center py-3">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <FrontFooter />
        <FilterMobileItem
          show={show}
          setShow={setShow}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          query={query}
          setQuery={setQuery}
          setPage={setPage}
        />
      </div>
    </>
  );
}

export default OurProduct;